/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Issue Operations
 * 
 * Handles issue grooming, code generation, archiving, and status updates.
 */

import { Issue, IssueType } from '@shared/types/issues';
import { LLMPromptRequest } from '@shared/types/requests/llm';
import { authenticatedFetch } from '@shared/lib/api';
import { getProductServerBaseUrl } from './base';
import { createWorktree } from './worktrees';
import { createVelocityCodeGenExecution, updateAgentExecutionStatus } from '../../lib/api/agent-executions';

export type IssueResult = { success: boolean; result?: string; error?: string; executionId?: string };

/**
 * Groom an issue using the "Groom Issue" prompt
 * Grooming operates directly on the velocity branch (main worktree) since it only
 * modifies product data in data/product/. Worktrees are not needed for grooming.
 */
export async function groomIssue(issueData: any, activeProduct?: any): Promise<IssueResult> {
  try {
    const promptRequest: LLMPromptRequest = {
      promptName: 'Groom Issue',
      params: {
        issueData: JSON.stringify(issueData),
        request: `Please groom the provided issue by improving it with additional technical context, relevant code references, and implementation details. Update the issue in issues.md with enhanced information to make it more actionable for developers.`,
        activeProduct: activeProduct || null,
      },
      returnRawText: true,
      velocityRunContext: {
        type: 'grooming' as const,
        entityType: 'issue' as const,
        entityId: issueData.id,
        productId: activeProduct?.id,
      },
    };

    console.log('[SkillShop Service] Grooming issue:', { issueId: issueData.id });

    const response = await authenticatedFetch('/api/llm/prompt', {
      method: 'POST',
      body: promptRequest,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API request failed (${response.status}): ${errorText}`);
    }

    const result = await response.text();
    const executionId = response.headers.get('X-Agent-Execution-Id') ?? undefined;

    console.log('[SkillShop Service] Issue grooming completed successfully');
    return {
      success: true,
      result: result,
      executionId,
    };
  } catch (error: any) {
    console.error('[SkillShop Service] Error grooming issue:', error);
    
    return {
      success: false,
      error: error.message || 'Failed to groom issue'
    };
  }
}

/**
 * Archive completed issues
 * Product server endpoint - uses direct fetch with repo context
 */
export async function archiveCompletedIssues(repoUrl?: string, repoPath?: string): Promise<IssueResult & { archivedCount?: number }> {
  try {
    console.log('[SkillShop Service] Archiving completed issues');

    const response = await fetch(`${getProductServerBaseUrl()}/api/data/issues/archive-completed`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ repoUrl, repoPath }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Archive request failed (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    
    console.log('[SkillShop Service] Issues archived successfully:', result);
    return {
      success: true,
      result: result.message,
      archivedCount: result.archivedCount
    };
  } catch (error: any) {
    console.error('[SkillShop Service] Error archiving completed issues:', error);
    
    return {
      success: false,
      error: error.message || 'Failed to archive completed issues'
    };
  }
}

/**
 * Update an issue's code log field
 * Product server endpoint - uses direct fetch with repo context
 */
export async function updateIssueCodeLog(
  issueId: string, 
  codeLog: string, 
  productId?: string, 
  repoUrl?: string, 
  repoPath?: string
): Promise<IssueResult> {
  try {
    // Extract the issue code (e.g., "TASK-002" from "TASK-002: Title")
    const issueCode = issueId.split(':')[0].trim();
    
    console.log('[SkillShop Service] Updating issue code log:', { issueCode, productId });

    // Build URL with productId and issueCode
    const effectiveProductId = productId || 'default';
    const baseUrl = `${getProductServerBaseUrl()}/api/issues/${encodeURIComponent(effectiveProductId)}/${encodeURIComponent(issueCode)}`;
    const params = new URLSearchParams();
    if (repoUrl) params.set('repoUrl', repoUrl);
    if (repoPath) params.set('repoPath', repoPath);
    const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ codeLog }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Code log update failed (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    
    console.log('[SkillShop Service] Issue code log updated successfully:', result);
    return {
      success: true,
      result: result.message
    };
  } catch (error: any) {
    console.error('[SkillShop Service] Error updating issue code log:', error);
    
    return {
      success: false,
      error: error.message || 'Failed to update issue code log'
    };
  }
}

/**
 * Update an issue's status
 * Product server endpoint - uses direct fetch with repo context
 */
export async function updateIssueStatus(
  issueId: string, 
  status: string, 
  productId?: string, 
  repoUrl?: string, 
  repoPath?: string
): Promise<IssueResult> {
  try {
    // Extract the issue code (e.g., "TASK-002" from "TASK-002: Title")
    const issueCode = issueId.split(':')[0].trim();
    
    console.log('[SkillShop Service] Updating issue status:', { issueCode, status, productId });

    // Build URL with productId and issueCode
    const effectiveProductId = productId || 'default';
    const baseUrl = `${getProductServerBaseUrl()}/api/issues/${encodeURIComponent(effectiveProductId)}/${encodeURIComponent(issueCode)}/status`;
    const params = new URLSearchParams();
    if (repoUrl) params.set('repoUrl', repoUrl);
    if (repoPath) params.set('repoPath', repoPath);
    const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Status update failed (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    
    console.log('[SkillShop Service] Issue status updated successfully:', result);
    return {
      success: true,
      result: result.message
    };
  } catch (error: any) {
    console.error('[SkillShop Service] Error updating issue status:', error);
    
    return {
      success: false,
      error: error.message || 'Failed to update issue status'
    };
  }
}

/**
 * Build a release using the code_agent tool
 * Creates a worktree for the operation if activeProduct has a repository
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function buildRelease(
  release: Issue,
  agent: string = 'claude-code',
  activeProduct?: any
): Promise<IssueResult> {
  let worktreePath: string | undefined;
  let codeGenExecutionId: string | undefined;

  try {
    console.log('[SkillShop Service] Building release using code agent:', release.id, 'with agent:', agent);

    // Create a worktree for this build operation if we have a repository
    if (activeProduct?.repositoryUrl) {
      const operation = `build-release-${release.id}-${Date.now()}`;
      const worktreeResult = await createWorktree(activeProduct.repositoryUrl, operation);
      
      if (worktreeResult.success && worktreeResult.worktreePath) {
        worktreePath = worktreeResult.worktreePath;
        console.log('[SkillShop Service] Using worktree for release build:', worktreePath);
      } else {
        console.warn('[SkillShop Service] Failed to create worktree, proceeding with main worktree:', worktreeResult.error);
      }
    }

    // Build the prompt for the coding agent
    const productTitle = activeProduct?.title || 'this';
    const releaseName = release.title || release.id;
    const prompt = `Review the user stories in data/product/user-stories and the technical documentation in docs/technical for the ${productTitle} product. Build ${releaseName} as defined in data/product/issues.md.`;
    
    // Build file context
    const files: string[] = [
      'data/product/issues.md',
      'data/product/user-stories/',
      'docs/technical/'
    ];
    
    if (release.links && release.links.length > 0) {
      files.push(...release.links);
    }

    console.log('[SkillShop Service] Release build context:', { 
      prompt, 
      fileCount: files.length,
      files,
      agent,
      worktreePath,
      activeProduct: activeProduct ? {
        id: activeProduct.id,
        productId: activeProduct.productId,
        title: activeProduct.title,
        repositoryUrl: activeProduct.repositoryUrl,
        repoPath: activeProduct.repoPath
      } : null
    });
    
    const codeAgentRequest = {
      prompt,
      files,
      agent,
      maxTurns: 100,
      timeout: 600000,
      workingDirectory: worktreePath,
      productContext: activeProduct ? {
        repositoryUrl: activeProduct.repositoryUrl,
        repoPath: activeProduct.repoPath
      } : undefined
    };

    const createRes = await createVelocityCodeGenExecution({
      releaseId: release.id,
      productId: activeProduct?.productId ?? activeProduct?.id,
      message: `Release build: ${release.id}`,
    });
    codeGenExecutionId = createRes.executionId;

    const response = await authenticatedFetch('/api/tools/code_agent/execute', {
      method: 'POST',
      body: {
        input: codeAgentRequest,
        context: {
          requestId: `build-release-${release.id}-${Date.now()}`,
          userId: 'developer',
          releaseId: release.id,
          releaseTitle: release.title
        }
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      const errorMessage = `Release build request failed (${response.status}): ${errorText}`;
      if (codeGenExecutionId) {
        await updateAgentExecutionStatus(codeGenExecutionId, { status: 'failed', result: { error: errorMessage } });
      }
      
      const errorLogEntry = `=== Release Build (${new Date().toISOString()}) ===
Agent: ${agent}
Status: Error
Duration: N/A
Turns: N/A
Cost: N/A

Error:
${errorMessage}
`;
      
      try {
        await updateIssueCodeLog(release.id, errorLogEntry, activeProduct?.productId, activeProduct?.repositoryUrl, activeProduct?.repoPath);
        await updateIssueStatus(release.id, 'Blocked', activeProduct?.productId, activeProduct?.repositoryUrl, activeProduct?.repoPath);
      } catch (logError) {
        console.error('[SkillShop Service] Failed to update error log:', logError);
      }
        
      throw new Error(errorMessage);
    }

    const result = await response.json();

    const toolResult = result.data?.result || result.result;
    const isError = toolResult?.is_error || false;
    const executionStatus = isError ? 'Error' : 'Success';
    if (codeGenExecutionId) {
      await updateAgentExecutionStatus(codeGenExecutionId, {
        status: isError ? 'failed' : 'completed',
        result: { success: !isError, result: toolResult?.result },
      });
    }

    console.log('[SkillShop Service] Release build completed:', release.id, {
      status: executionStatus,
      agent: toolResult?.agent,
      costUsd: toolResult?.cost_usd,
      durationMs: toolResult?.duration_ms,
      numTurns: toolResult?.num_turns
    });

    const codeLogEntry = `=== Release Build (${new Date().toISOString()}) ===
Agent: ${toolResult?.agent || agent}
Status: ${executionStatus}
Duration: ${toolResult?.duration_ms ? `${(toolResult.duration_ms / 1000).toFixed(2)}s` : 'N/A'}
Turns: ${toolResult?.num_turns || 'N/A'}
Cost: ${toolResult?.cost_usd ? `$${toolResult.cost_usd.toFixed(4)}` : 'N/A'}

${isError ? 'Error' : 'Output'}:
${toolResult?.result || 'No output available'}
`;

    try {
      await updateIssueCodeLog(release.id, codeLogEntry, activeProduct?.productId, activeProduct?.repositoryUrl, activeProduct?.repoPath);
      
      if (isError) {
        await updateIssueStatus(release.id, 'Blocked', activeProduct?.productId, activeProduct?.repositoryUrl, activeProduct?.repoPath);
      } else {
        await updateIssueStatus(release.id, 'Ready For Review', activeProduct?.productId, activeProduct?.repositoryUrl, activeProduct?.repoPath);
      }
    } catch (error) {
      console.error('[SkillShop Service] Failed to update code log or status:', error);
    }
    
    return {
      success: !isError,
      result: toolResult?.result || (isError ? 'Release build failed' : 'Release build completed successfully'),
      error: isError ? toolResult?.result : undefined
    };
  } catch (error: any) {
    console.error('[SkillShop Service] Error building release:', error);
    if (codeGenExecutionId) {
      await updateAgentExecutionStatus(codeGenExecutionId, {
        status: 'failed',
        result: { error: error.message || 'Failed to build release' },
      });
    }
    const exceptionLogEntry = `=== Release Build (${new Date().toISOString()}) ===
Agent: ${agent}
Status: Exception
Duration: N/A
Turns: N/A
Cost: N/A

Exception:
${error.message || 'Failed to build release'}

Stack trace:
${error.stack || 'No stack trace available'}
`;

    try {
      await updateIssueCodeLog(release.id, exceptionLogEntry, activeProduct?.productId, activeProduct?.repositoryUrl, activeProduct?.repoPath);
      await updateIssueStatus(release.id, 'Blocked', activeProduct?.productId, activeProduct?.repositoryUrl, activeProduct?.repoPath);
    } catch (logError) {
      console.error('[SkillShop Service] Failed to update exception log:', logError);
    }

    return {
      success: false,
      error: error.message || 'Failed to build release'
    };
  }
}

/**
 * Implement an issue using the code_agent tool
 * Creates a worktree for the operation if activeProduct has a repository
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function generateCodeForIssue(
  issue: Issue,
  agent: string = 'claude-code',
  activeProduct?: any
): Promise<IssueResult> {
  let worktreePath: string | undefined;
  let codeGenExecutionId: string | undefined;

  try {
    console.log('[SkillShop Service] Implementing issue using code agent:', issue.id, 'with agent:', agent);

    // Create a worktree for this code generation operation if we have a repository
    if (activeProduct?.repositoryUrl) {
      const operation = `code-gen-${issue.id}-${Date.now()}`;
      const worktreeResult = await createWorktree(activeProduct.repositoryUrl, operation);
      
      if (worktreeResult.success && worktreeResult.worktreePath) {
        worktreePath = worktreeResult.worktreePath;
        console.log('[SkillShop Service] Using worktree for code generation:', worktreePath);
      } else {
        console.warn('[SkillShop Service] Failed to create worktree, proceeding with main worktree:', worktreeResult.error);
      }
    }

    // Build the prompt based on issue type
    const promptMap: Record<IssueType, string> = {
      'Bug': `Fix bug \"${issue.id}\"`,
      'Task': `Implement task \"${issue.id}\"`,
      'Feature': `Implement feature \"${issue.id}\"`,
      'Release': `Prepare release \"${issue.id}\"`
    };

    const prompt = promptMap[issue.type] || `Implement ${issue.id}`;

    const repoPath = activeProduct?.repoPath;
    const basePath = repoPath ? `${repoPath.replace(/\/+$/, '')}/` : '';
    const files: string[] = [
      `${basePath}docs/ISSUE_TRACKING.md`,
      `${basePath}data/product/issues.md`,
    ];

    if (issue.links && issue.links.length > 0) {
      files.push(...issue.links);
    }

    console.log('[SkillShop Service] Code agent context:', { 
      prompt, 
      fileCount: files.length,
      files,
      agent,
      worktreePath,
      activeProduct: activeProduct ? {
        id: activeProduct.id,
        productId: activeProduct.productId,
        repositoryUrl: activeProduct.repositoryUrl,
        repoPath: activeProduct.repoPath
      } : null
    });
    
    const codeAgentRequest = {
      prompt,
      files,
      agent,
      maxTurns: 100,
      timeout: 600000,
      workingDirectory: worktreePath,
      productContext: activeProduct ? {
        repositoryUrl: activeProduct.repositoryUrl,
        repoPath: activeProduct.repoPath
      } : undefined
    };

    const createRes = await createVelocityCodeGenExecution({
      issueId: issue.id,
      productId: activeProduct?.productId ?? activeProduct?.id,
      message: `Fix it/Build it: ${issue.id}`,
    });
    codeGenExecutionId = createRes.executionId;

    const response = await authenticatedFetch('/api/tools/code_agent/execute', {
      method: 'POST',
      body: {
        input: codeAgentRequest,
        context: {
          requestId: `code-agent-${issue.id}-${Date.now()}`,
          userId: 'developer',
          issueId: issue.id,
          issueType: issue.type,
          productId: activeProduct?.productId ?? activeProduct?.id
        }
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      const errorMessage = `Code agent request failed (${response.status}): ${errorText}`;
      if (codeGenExecutionId) {
        await updateAgentExecutionStatus(codeGenExecutionId, { status: 'failed', result: { error: errorMessage } });
      }
      const errorLogEntry = `=== Code Agent Execution (${new Date().toISOString()}) ===
Agent: ${agent}
Status: Error
Duration: N/A
Turns: N/A
Cost: N/A

Error:
${errorMessage}
`;

      try {
        await updateIssueCodeLog(issue.id, errorLogEntry, activeProduct?.productId, activeProduct?.repositoryUrl, activeProduct?.repoPath);
        await updateIssueStatus(issue.id, 'Blocked', activeProduct?.productId, activeProduct?.repositoryUrl, activeProduct?.repoPath);
      } catch (logError) {
        console.error('[SkillShop Service] Failed to update error log:', logError);
      }

      throw new Error(errorMessage);
    }

    const result = await response.json();

    const toolResult = result.data?.result || result.result;
    const isError = toolResult?.is_error || false;
    const executionStatus = isError ? 'Error' : 'Success';
    if (codeGenExecutionId) {
      await updateAgentExecutionStatus(codeGenExecutionId, {
        status: isError ? 'failed' : 'completed',
        result: { success: !isError, result: toolResult?.result },
      });
    }

    console.log('[SkillShop Service] Issue code generation completed:', issue.id, {
      status: executionStatus,
      agent: toolResult?.agent,
      costUsd: toolResult?.cost_usd,
      durationMs: toolResult?.duration_ms,
      numTurns: toolResult?.num_turns
    });
    
    const codeLogEntry = `=== Code Agent Execution (${new Date().toISOString()}) ===
Agent: ${toolResult?.agent || agent}
Status: ${executionStatus}
Duration: ${toolResult?.duration_ms ? `${(toolResult.duration_ms / 1000).toFixed(2)}s` : 'N/A'}
Turns: ${toolResult?.num_turns || 'N/A'}
Cost: ${toolResult?.cost_usd ? `$${toolResult.cost_usd.toFixed(4)}` : 'N/A'}

${isError ? 'Error' : 'Output'}:
${toolResult?.result || 'No output available'}
`;

    try {
      await updateIssueCodeLog(issue.id, codeLogEntry, activeProduct?.productId, activeProduct?.repositoryUrl, activeProduct?.repoPath);
      
      if (isError) {
        await updateIssueStatus(issue.id, 'Blocked', activeProduct?.productId, activeProduct?.repositoryUrl, activeProduct?.repoPath);
      } else {
        await updateIssueStatus(issue.id, 'Ready For Review', activeProduct?.productId, activeProduct?.repositoryUrl, activeProduct?.repoPath);
      }
    } catch (error) {
      console.error('[SkillShop Service] Failed to update code log or status:', error);
    }
    
    return {
      success: !isError,
      result: toolResult?.result || (isError ? 'Code generation failed' : 'Implementation completed successfully'),
      error: isError ? toolResult?.result : undefined
    };
  } catch (error: any) {
    console.error('[SkillShop Service] Error implementing issue:', error);
    if (codeGenExecutionId) {
      await updateAgentExecutionStatus(codeGenExecutionId, {
        status: 'failed',
        result: { error: error.message || 'Failed to implement issue' },
      });
    }
    const exceptionLogEntry = `=== Code Agent Execution (${new Date().toISOString()}) ===
Agent: ${agent}
Status: Exception
Duration: N/A
Turns: N/A
Cost: N/A

Exception:
${error.message || 'Failed to implement issue'}

Stack trace:
${error.stack || 'No stack trace available'}
`;

    try {
      await updateIssueCodeLog(issue.id, exceptionLogEntry, activeProduct?.productId, activeProduct?.repositoryUrl, activeProduct?.repoPath);
      await updateIssueStatus(issue.id, 'Blocked', activeProduct?.productId, activeProduct?.repositoryUrl, activeProduct?.repoPath);
    } catch (logError) {
      console.error('[SkillShop Service] Failed to update exception log:', logError);
    }
    
    return {
      success: false,
      error: error.message || 'Failed to implement issue'
    };
  }
}

